/**
 * Reads + writes the caller's verification rows.
 *
 * Reads: all rows for the signed-in tester. Refetched on every mutation,
 * cached otherwise. The dataset is small (a tester at most has roughly
 * 5 + N + 4M + M + 4 rows where N=challenges, M=days; for 3 challenges
 * over 7 days that's ~50 rows).
 *
 * Writes: upsert by (tester_email, condition_key, testing_day). Tester
 * email is taken from the JWT-derived auth context, NOT from the client,
 * because RLS will reject any row where tester_email != auth.jwt()->>email.
 */

import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import type { Verification, VerificationStatus } from "../types";

interface UpsertInput {
  conditionKey: string;
  testingDay: number;
  status: VerificationStatus;
}

interface VerificationsState {
  loading: boolean;
  /** All verification rows for the current tester. */
  verifications: Verification[];
  /**
   * Look up the status of (condition_key, testing_day). Returns null if no
   * row exists yet. Reads from the in-memory cache; no network call.
   */
  getStatus: (conditionKey: string, testingDay: number) => Verification | null;
  /** Upsert a verification row. Optimistic UI: cache updates before the round-trip. */
  setStatus: (input: UpsertInput) => Promise<void>;
}

export function useVerifications(testerEmail: string | null): VerificationsState {
  const queryClient = useQueryClient();
  const queryKey = ["verifications", testerEmail];

  const query = useQuery<Verification[]>({
    queryKey,
    enabled: !!testerEmail,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("verifications")
        .select("*")
        .eq("tester_email", testerEmail!);
      if (error) throw error;
      return (data ?? []) as Verification[];
    },
    staleTime: 15_000,
  });

  const verifications = query.data ?? [];

  const getStatus = useCallback(
    (conditionKey: string, testingDay: number): Verification | null => {
      return (
        verifications.find(
          (v) => v.condition_key === conditionKey && v.testing_day === testingDay,
        ) ?? null
      );
    },
    [verifications],
  );

  const mutation = useMutation({
    mutationFn: async (input: UpsertInput) => {
      if (!testerEmail) throw new Error("Cannot save without a signed-in tester");
      const row = {
        tester_email: testerEmail,
        condition_key: input.conditionKey,
        testing_day: input.testingDay,
        status: input.status,
      };
      const { data, error } = await supabase
        .from("verifications")
        .upsert(row, { onConflict: "tester_email,condition_key,testing_day" })
        .select()
        .single();
      if (error) throw error;
      return data as Verification;
    },
    onMutate: async (input) => {
      // Optimistic update: replace or append the row in the cache.
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Verification[]>(queryKey) ?? [];
      const filtered = previous.filter(
        (v) =>
          !(v.condition_key === input.conditionKey && v.testing_day === input.testingDay),
      );
      const optimistic: Verification = {
        id: "optimistic",
        tester_email: testerEmail!,
        condition_key: input.conditionKey,
        testing_day: input.testingDay,
        status: input.status,
        cleared_at: null,
        cleared_by: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      queryClient.setQueryData<Verification[]>(queryKey, [...filtered, optimistic]);
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const setStatus = useCallback(
    async (input: UpsertInput) => {
      await mutation.mutateAsync(input);
    },
    [mutation],
  );

  return {
    loading: query.isLoading,
    verifications,
    getStatus,
    setStatus,
  };
}
